import React, { useState, useCallback, useEffect } from 'react';
import { Form, Button, Icon, Message, Segment, Label, Header } from 'semantic-ui-react';

const RcmWebhookConfig = React.memo(
  ({ config, accessToken, serverBaseUrl, onConfigUpdate, isLoading }) => {
    const settings = config?.settings || {};
    const [webhookEnabled, setWebhookEnabled] = useState(settings.webhookEnabled || false);
    const [webhookOwnerId, setWebhookOwnerId] = useState(settings.webhookOwnerId || '');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
      const s = config?.settings || {};
      setWebhookEnabled(s.webhookEnabled || false);
      setWebhookOwnerId(s.webhookOwnerId || '');
    }, [config?.settings]);

    const webhookToken = settings.webhookToken || '';
    const baseUrl = window.location.origin;
    const webhookUrl = webhookToken
      ? `${baseUrl}/api/webhooks/rcm?ownerId=${encodeURIComponent(
          webhookOwnerId
        )}&token=${webhookToken}&reservationno={Reservation.ReservationNo}&reservationID={Reservation.RcmReferenceId}`
      : '';

    const handleCopyUrl = useCallback(() => {
      navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }, [webhookUrl]);

    const handleSave = useCallback(async () => {
      onConfigUpdate({
        webhookEnabled,
        webhookOwnerId: webhookOwnerId.trim(),
      });
    }, [webhookEnabled, webhookOwnerId, onConfigUpdate]);

    const handleRegenerateToken = useCallback(async () => {
      try {
        const response = await fetch(`${serverBaseUrl}/api/integrations/rcm/configure`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            type: 'rcm_bookings',
            webhookEnabled: true,
            webhookOwnerId: webhookOwnerId.trim(),
            webhookToken: 'regenerate',
          }),
        });
        if (response.ok) {
          const data = await response.json();
          if (data.item?.settings?.webhookToken) {
            onConfigUpdate({ webhookEnabled: true });
          }
        }
      } catch (err) {
        console.error('Error regenerating token:', err);
      }
    }, [accessToken, serverBaseUrl, webhookOwnerId, onConfigUpdate]);

    return (
      <Segment>
        <Header as="h4" style={{ marginBottom: '15px' }}>
          <Icon name="linkify" />
          <Header.Content>
            Webhook Configuration
            <Header.Subheader>Receive real-time booking changes from RCM</Header.Subheader>
          </Header.Content>
        </Header>

        <Form>
          <Form.Field>
            <Form.Checkbox
              toggle
              label="Enable Webhook Receiver"
              checked={webhookEnabled}
              onChange={(e, { checked }) => setWebhookEnabled(checked)}
              disabled={isLoading}
            />
          </Form.Field>

          {webhookEnabled && (
            <>
              <Form.Field>
                <label>RCM Owner ID</label>
                <input
                  type="text"
                  placeholder="e.g. rBj00dGH1lex5UPh9zOZzdIsG2c2"
                  value={webhookOwnerId}
                  onChange={e => setWebhookOwnerId(e.target.value)}
                  disabled={isLoading}
                  style={{ fontFamily: 'monospace' }}
                />
                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                  The ownerId parameter that RCM sends to identify your account
                </small>
              </Form.Field>

              {webhookToken && webhookOwnerId && (
                <Form.Field>
                  <label>Webhook URL</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <div
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: '#f5f5f5',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        fontFamily: 'monospace',
                        fontSize: '0.85em',
                        wordBreak: 'break-all',
                        lineHeight: '1.5',
                      }}
                    >
                      {webhookUrl}
                    </div>
                    <Button
                      icon
                      size="small"
                      onClick={handleCopyUrl}
                      title="Copy URL"
                      color={copied ? 'green' : undefined}
                    >
                      <Icon name={copied ? 'check' : 'copy'} />
                    </Button>
                  </div>
                </Form.Field>
              )}

              {webhookToken && (
                <Form.Field>
                  <label>Security Token</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Label basic style={{ fontFamily: 'monospace' }}>
                      {webhookToken.substring(0, 8)}...
                      {webhookToken.substring(webhookToken.length - 4)}
                    </Label>
                    <Button
                      size="tiny"
                      onClick={handleRegenerateToken}
                      disabled={isLoading}
                      icon="refresh"
                      content="Regenerate"
                    />
                  </div>
                </Form.Field>
              )}

              <Message info size="small">
                <Message.Header>Configure in RCM</Message.Header>
                <p>
                  Set this full URL as the webhook target in your RCM account settings. RCM will
                  call this URL whenever a booking is created or updated.
                </p>
              </Message>
            </>
          )}

          <Button
            primary
            size="small"
            onClick={handleSave}
            loading={isLoading}
            disabled={isLoading || (webhookEnabled && !webhookOwnerId.trim())}
            content="Save Webhook Settings"
            icon="save"
            style={{ marginTop: '10px' }}
          />
        </Form>
      </Segment>
    );
  }
);

RcmWebhookConfig.displayName = 'RcmWebhookConfig';

export default RcmWebhookConfig;
